import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import appNoticeService from '../../services/appNoticeService';
import { resolveAppLink, kindColors, rowBackground, GROUP_TITLES, STATIC_PAGES } from '../../utils/resolveAppLink';
import { SearchIcon, Icon, ICON } from '../broadcast/studioUi';

/**
 * GOES TO / OPENS ON TAP — one searchable field over pages + content, a manual `/path`, or an
 * absolute `https://` URL, with a strip under it that says exactly where the link resolves.
 * design_handoff_app_notices "Link picker".
 *
 * `value` is what gets saved (the picker rewrites an own-domain URL to its path on pick).
 * `mode` is sheet | push (what blank means). `onResolved(res)` reports every resolution so the
 * composer can gate publish on `res.ok` — the rules in `utils/resolveAppLink` decide at once,
 * and a path that needs a lookup (an event, a club, a handle) is confirmed by the server
 * (`GET app-notices/admin/links/resolve`), which runs the same resolver on save.
 */
const initials = (name) => (name || '').replace(/^(City Portal|Events in) · ?/, '').split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase();

export default function LinkPicker({ value, onChange, mode = 'sheet', onResolved, id, testId = 'link-picker' }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [results, setResults] = useState(null);
  const [server, setServer] = useState(null); // { path, res } — the last lookup answer
  const timer = useRef(null);
  const lookupSeq = useRef(0);

  // The base catalog (a few of each) loads once; a query asks again, debounced.
  useEffect(() => {
    let cancelled = false;
    (async () => { const r = await appNoticeService.links(''); if (!cancelled && r.success) setCatalog(r.data || []); })();
    return () => { cancelled = true; };
  }, []);
  const q = (value || '').trim();
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.startsWith('/') || /^https?:/i.test(q)) { setResults(null); return undefined; }
    timer.current = setTimeout(async () => { const r = await appNoticeService.links(q); setResults(r.success ? (r.data || []) : []); }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const known = useMemo(() => [...catalog, ...(results || [])], [catalog, results]);
  const local = useMemo(() => resolveAppLink(value, { mode, catalog: known }), [value, mode, known]);
  const res = server && server.path === local.path && local.pending ? server.res : local;

  // A pending resolution (event / club / Pro page / handle not in the catalog) asks the server.
  useEffect(() => {
    if (!local.pending || !local.path) return undefined;
    const seq = ++lookupSeq.current;
    const t = setTimeout(async () => {
      const r = await appNoticeService.resolveLink(local.path, mode);
      if (seq !== lookupSeq.current) return;
      setServer({ path: local.path, res: r.success && r.data ? { ...r.data, pending: false } : { ...local, pending: false, ok: false, kind: 'CHECK', label: 'Could not check this link', note: 'Try again in a moment.' } });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.pending, local.path, mode]);

  useEffect(() => { onResolved?.(res); }, [res, onResolved]);

  const lower = q.toLowerCase();
  const pool = results ? [...STATIC_PAGES, ...results] : [...STATIC_PAGES, ...catalog];
  const matches = pool.filter((c) => !lower || c.label.toLowerCase().includes(lower) || c.path.includes(lower));
  const groups = Object.keys(GROUP_TITLES).map((k) => ({
    key: k,
    title: GROUP_TITLES[k],
    items: matches.filter((c) => c.kind === k).slice(0, lower ? 6 : (k === 'PAGE' ? 8 : 3)),
  })).filter((g) => g.items.length);
  const exact = pool.some((c) => c.path === q);
  const manual = !!q && !exact;
  const none = open && groups.length === 0 && !manual;
  const [pillBg, pillFg] = kindColors(res.kind);

  const pick = useCallback((e, path) => { e.preventDefault(); onChange(path); setOpen(false); }, [onChange]);
  const onKey = (e) => { if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); setOpen(false); e.target.blur(); } };

  return (
    <div className="relative flex flex-col" style={{ gap: 8 }} data-testid={testId}>
      <div className="relative">
        <div className="flex items-center" style={{ gap: 10, height: 46, padding: '0 6px 0 16px', boxSizing: 'border-box', border: `2px solid ${open ? '#000' : 'transparent'}`, borderRadius: 14, background: open ? '#fff' : '#F5F5F5' }}>
          <SearchIcon />
          <input id={id} value={value || ''} onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)} onKeyDown={onKey}
            placeholder="Search pages, or paste /path or https://" aria-label="Link" autoComplete="off"
            className="min-w-0 flex-1 border-0 bg-transparent font-mono text-[13px] outline-none focus:ring-0" style={{ height: '100%', padding: 0 }} />
          {!!value && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); onChange(''); }} aria-label="Clear link" className="flex flex-shrink-0 items-center justify-center rounded-full hover:bg-[#EDEDED]" style={{ width: 32, height: 32 }}>
              <Icon d={ICON.close} size={12} />
            </button>
          )}
        </div>
        {open && (
          <div role="listbox" className="absolute left-0 right-0 flex flex-col overflow-y-auto bg-white" style={{ top: 'calc(100% + 6px)', zIndex: 30, maxHeight: 360, gap: 4, padding: 8, borderRadius: 22, boxShadow: '0 0 0 2px #0A0A0A' }}>
            {manual && (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); setOpen(false); }} className="flex items-center text-left hover:bg-[#EDEDED]" style={{ gap: 12, padding: '8px 12px 8px 8px', borderRadius: 16, background: '#F5F5F5' }}>
                <span className="flex flex-shrink-0 items-center justify-center rounded-full font-mono text-[14px] font-extrabold" style={{ width: 36, height: 36, background: pillBg, color: pillFg }}>↵</span>
                <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                  <span className="font-mono text-[10px] font-extrabold tracking-[0.14em]">USE WHAT YOU TYPED</span>
                  <span className="truncate font-mono text-[11px] text-[#6B7280]">{value}</span>
                </span>
                <span className="flex items-center whitespace-nowrap rounded-full font-mono text-[9px] font-extrabold tracking-[0.12em]" style={{ height: 22, padding: '0 9px', background: pillBg, color: pillFg }}>{res.kind}</span>
              </button>
            )}
            {groups.map((g) => (
              <React.Fragment key={g.key}>
                <span className="font-mono text-[9px] font-extrabold tracking-[0.18em] text-[#9CA3AF]" style={{ padding: '10px 10px 4px' }}>{g.title}</span>
                {g.items.map((c) => {
                  const [dot] = kindColors(c.kind);
                  const isPage = c.kind === 'PAGE';
                  return (
                    <button key={c.path} type="button" role="option" aria-selected={c.path === value} onMouseDown={(e) => pick(e, c.path)} className="flex items-center text-left hover:bg-[#F2F2F2]"
                      style={{ gap: 12, padding: '6px 12px 6px 6px', borderRadius: 16, background: c.path === value ? '#F2F2F2' : 'transparent' }}>
                      <span className="flex flex-shrink-0 items-center justify-center font-mono text-[9px] font-extrabold text-black" style={{ width: 36, height: 36, borderRadius: c.kind === 'EVENT' ? 10 : '50%', background: `${c.img ? '#EDEDED' : isPage ? '#F2F2F2' : dot} center/cover no-repeat`, backgroundImage: c.img ? `url(${c.img})` : 'none' }}>
                        {!c.img && (isPage ? '/' : initials(c.label))}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                        <span className="truncate font-mono text-[12px] font-bold">{c.label}</span>
                        <span className="truncate font-mono text-[10px] text-[#6B7280]">{c.path}{c.sub ? ` · ${c.sub}` : ''}</span>
                      </span>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
            {none && <span className="font-mono text-[10px] leading-[1.6] tracking-[0.06em] text-[#6B7280]" style={{ padding: '14px 10px' }}>No pages match. Type a full path like /clubs/da-gang or a link like https://example.com.</span>}
          </div>
        )}
      </div>
      <div className="flex items-center" style={{ gap: 10, minHeight: 36, padding: '6px 14px 6px 6px', boxSizing: 'border-box', borderRadius: 999, background: rowBackground(res) }} data-testid="link-resolved" data-ok={res.ok ? 'true' : 'false'} data-kind={res.kind}>
        <span className="flex flex-shrink-0 items-center rounded-full font-mono text-[9px] font-extrabold tracking-[0.14em]" style={{ height: 24, padding: '0 10px', background: pillBg, color: pillFg }}>{res.kind}</span>
        <span className="min-w-0 flex-1 font-mono text-[11px] leading-[1.4]"><b>{res.label}</b> <span className="text-[#6B7280]">{res.note}</span></span>
      </div>
    </div>
  );
}
